import {
  NoteImageDisplayState,
  NoteImageErrorCode,
  NoteImageMimeType
} from 'enums';

const ONE_MINUTE_MS = 60_000;

/** Limits and formats from the backend contract (backend docs/note-image-attachments.md). */
export const NOTE_IMAGE = {
  REFERENCE_SCHEME: 'fb-note-image://',
  /** Global: use only with `matchAll`, which copies it. */
  REFERENCE_PATTERN: /fb-note-image:\/\/([0-9a-fA-F-]{36})/g,
  /** Pasted inline images that an editor uploads, turning them into references. */
  INLINE_SRC_PATTERN: /^(?:data:image\/|blob:)/i,
  REMOTE_SRC_PATTERN: /^(?:https?:)?\/\//i,
  MAX_FILE_SIZE_BYTES: 10_485_760,
  MAX_LONG_EDGE_PX: 2560,
  REENCODE_QUALITY: 0.9,
  /** Refresh a signed URL this long before `url_expires_at`. */
  URL_REFRESH_MARGIN_MS: 5 * ONE_MINUTE_MS,
  /** Stops a skewed device clock from turning the refresh timer into a request loop. */
  MIN_REFRESH_DELAY_MS: ONE_MINUTE_MS / 2,
  COMPLETE_RETRY: {
    ATTEMPTS: 3,
    BASE_DELAY_MS: 500
  },
  UPLOADABLE_MIME_TYPES: [
    NoteImageMimeType.PNG,
    NoteImageMimeType.JPEG,
    NoteImageMimeType.GIF,
    NoteImageMimeType.WEBP
  ],
  /** Converted to JPEG on the device before upload; the server rejects them. */
  CONVERTIBLE_MIME_TYPES: [NoteImageMimeType.HEIC, NoteImageMimeType.HEIF],
  CONVERTED_FILE_EXTENSION: '.jpg',
  PLACEHOLDER: {
    WIDTH: 320,
    HEIGHT: 120
  },
  EDITOR_ATTR: {
    IMAGE_ID: 'data-fb-note-image-id',
    STATE: 'data-fb-note-image-state',
    UPLOAD_ID: 'data-fb-note-image-upload-id'
  }
} as const;

export const NOTE_IMAGE_ACCEPT_ATTRIBUTE = [
  ...NOTE_IMAGE.UPLOADABLE_MIME_TYPES,
  ...NOTE_IMAGE.CONVERTIBLE_MIME_TYPES
].join(',');

/** CKEditor `image.upload.types` takes the subtype after `image/`. */
export const NOTE_IMAGE_CKEDITOR_UPLOAD_TYPES = [
  'png',
  'jpeg',
  'gif',
  'webp',
  'heic',
  'heif'
];

export const NOTE_IMAGE_ERROR_MESSAGE_KEYS: Record<NoteImageErrorCode, string> =
  {
    [NoteImageErrorCode.UNSUPPORTED_TYPE]:
      'note_images.errors.unsupported_type',
    [NoteImageErrorCode.TOO_LARGE]: 'note_images.errors.too_large',
    [NoteImageErrorCode.NOT_UPLOADED]: 'note_images.errors.not_uploaded',
    [NoteImageErrorCode.LIMIT_REACHED]: 'note_images.errors.limit_reached',
    [NoteImageErrorCode.FEATURE_UNAVAILABLE]: 'note_images.errors.unavailable',
    [NoteImageErrorCode.FORBIDDEN]: 'note_images.errors.forbidden',
    [NoteImageErrorCode.TITLE_REQUIRED]: 'note_images.errors.title_required',
    [NoteImageErrorCode.UPLOAD_FAILED]: 'note_images.errors.upload_failed'
  };

export const NOTE_IMAGE_DISPLAY_LABEL_KEYS: Record<
  NoteImageDisplayState,
  string
> = {
  [NoteImageDisplayState.LOADING]: 'note_images.loading',
  [NoteImageDisplayState.READY]: 'note_images.image',
  [NoteImageDisplayState.UNAVAILABLE]: 'note_images.unavailable',
  [NoteImageDisplayState.EXTERNAL]: 'note_images.external_image'
};
